import { useParams } from 'react-router-dom';

import { replayCounter } from '@/core/engine/x01/replay';
import type { Side } from '@/core/engine/x01/types';
import { matchIdSchema } from '@/core/schemas';
import { CounterSection } from '@/features/counter/CounterSection';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { sideOfCaptain } from '@/infrastructure/repositories/matchRepository';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Alert } from '@/shared/ui/Alert';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { MATCH_STATUS_LABELS, describeRules, scoreLabel, sideLabel } from './matchPresentation';

export function MatchDetailPage() {
  usePageTitle('Spiel');
  const params = useParams();
  const { tournament, matches, myTeam, lookup, rulesFor, isLoading, errorMessage } =
    useTournamentData();

  const matchId = matchIdSchema.safeParse(params.matchId);
  const match = matchId.success ? matches.find((entry) => entry.id === matchId.data) : undefined;

  if (isLoading) {
    return <LoadingScreen label="Spiel wird geladen …" />;
  }

  if (errorMessage !== null || tournament === undefined) {
    return <PageState title="Spiel nicht verfügbar" description={errorMessage ?? ''} />;
  }

  if (match === undefined) {
    return <PageState title="Spiel nicht gefunden" description="Dieses Spiel existiert nicht." />;
  }

  const rules = rulesFor(match);
  const ownSide: Side | null = myTeam === null ? null : sideOfCaptain(match, myTeam.id);
  const game =
    match.counter !== null && rules !== null ? replayCounter(match.counter, rules) : null;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header className="flex flex-col gap-1">
        <p className="text-fg-subtle text-xs tracking-widest uppercase">
          {describeRules(rules)} · {tournament.settings.game.startScore}{' '}
          {tournament.settings.game.doubleOut ? 'Double-Out' : 'Single-Out'} ·{' '}
          {MATCH_STATUS_LABELS[match.status]}
        </p>
        <h1 className="text-2xl font-bold tracking-tight">
          {sideLabel(match.home, lookup)} <span className="text-fg-subtle">gegen</span>{' '}
          {sideLabel(match.away, lookup)}
        </h1>
      </header>

      {match.status === 'completed' ? (
        <Alert tone="success">
          Endergebnis: {scoreLabel(match)}. Sieger:{' '}
          {match.result?.winnerTeamId == null
            ? 'unentschieden'
            : (lookup(match.result.winnerTeamId)?.name ?? '—')}
          .
        </Alert>
      ) : match.status === 'conflict' ? (
        <Alert tone="danger">
          <p className="font-semibold">Ergebnis angezweifelt.</p>
          <p className="mt-1 text-xs">
            Die Turnierleitung klärt dieses Spiel. Bis dahin bleibt es offen.
          </p>
        </Alert>
      ) : rules === null ? (
        <Alert tone="danger">Der Spielmodus konnte nicht ermittelt werden.</Alert>
      ) : (
        <CounterSection
          match={match}
          game={game}
          startScore={tournament.settings.game.startScore}
          doubleOut={tournament.settings.game.doubleOut}
          lookup={lookup}
          ownSide={ownSide}
        />
      )}
    </div>
  );
}
